import { GridItem } from '@chakra-ui/react'
import { Body, Dropdown, TimePicker } from '@missionlabs/react'
import { NotAvailable, NotAvailableDayTimes } from '@missionlabs/types'
import { format, parse } from 'date-fns'
import { ChangeEvent, FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDebounce } from 'react-use'

import { Availability, getAvailabilityOptions, getTimeOptions, TimeSpan } from './scheduleOptions'

export interface ScheduleDataRowProps {
    data: NotAvailableDayTimes | undefined
    day: string
    availability?: string
    time?: string
    from?: string
    to?: string
    onUpdate: (payload?: NotAvailable) => void
}

const timeRegex = /^\d{2}:\d{2}$/

// Returns the HH:mm portion of the time string (e.g. "12:00:00" -> "12:00")
const getTimePortion = (time?: string) => time?.slice(0, 5)

export const ScheduleDataRow: FC<ScheduleDataRowProps> = ({
    data,
    day,
    availability = Availability.AVAILABLE,
    time = TimeSpan.ALL_DAY,
    from,
    to,
    onUpdate
}) => {
    const { t } = useTranslation()

    const [localFrom, setLocalFrom] = useState(getTimePortion(from) ?? '00:00')
    const [localTo, setLocalTo] = useState(getTimePortion(to) ?? '00:00')

    // If the from or to time changes, we want to update the local state
    // FIXES: https://missionlabs.atlassian.net/browse/PL-268
    useEffect(() => {
        setLocalFrom(getTimePortion(from) ?? '00:00')
        setLocalTo(getTimePortion(to) ?? '00:00')
    }, [from, to])

    const isAlwaysOn = useMemo(() => time === TimeSpan.ALL_DAY, [time])
    const isUnavailable = useMemo(() => availability === Availability.UNAVAILABLE, [availability])

    const onUpdateAvailability = useCallback(
        (availability: Availability) => {
            const payload = data && Object.keys(data).length ? { ...data } : undefined
            onUpdate({ [day]: availability === Availability.AVAILABLE ? payload : {} })
        },
        [data, day, onUpdate]
    )

    const onUpdateTime = useCallback(
        (timeSpan: TimeSpan) => {
            let updateData: NotAvailableDayTimes | undefined = { ...data }
            if (timeSpan === TimeSpan.ALL_DAY) {
                // If they change to "All day" then we want to remove this day from the "notAvailable"
                // otherwise the availability will change to "Unavailable"
                updateData = undefined
            } else {
                updateData.before = data?.before ?? '00:00:00'
                updateData.after = data?.after ?? '00:00:00'
            }
            onUpdate({ [day]: updateData })
        },
        [data, day, onUpdate]
    )

    useDebounce(
        () => {
            if (!localFrom) return
            if (isAlwaysOn || isUnavailable) {
                // Cancel the debounce if the time is always on or unavailable
                // so that the time is not updated
                return
            }
            const parsed = parse(localFrom, 'HH:mm', new Date())
            const before = format(parsed, 'HH:mm:ss')
            if (before !== from) onUpdate({ [day]: { ...data, before } })
        },
        1000,
        [localFrom, day, onUpdate, isAlwaysOn, isUnavailable]
    )

    useDebounce(
        () => {
            if (!localTo) return
            if (isAlwaysOn || isUnavailable) {
                // Cancel the debounce if the time is always on or unavailable
                // so that the time is not updated
                return
            }
            const parsed = parse(localTo, 'HH:mm', new Date())
            const after = format(parsed, 'HH:mm:ss')
            if (after !== to) onUpdate({ [day]: { ...data, after } })
        },
        1000,
        [localTo, day, onUpdate, isAlwaysOn, isUnavailable]
    )

    const onUpdateFrom = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        if (value && timeRegex.test(value)) setLocalFrom(value)
    }, [])

    const onUpdateTo = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        if (value && timeRegex.test(value)) setLocalTo(value)
    }, [])

    return (
        <>
            <GridItem display="flex" alignItems="center">
                <Body size="xs" variant="bold">
                    {t(`calls.days.${day}`)}
                </Body>
            </GridItem>
            <GridItem>
                <Dropdown
                    aria-label="availability"
                    value={availability}
                    options={getAvailabilityOptions(t)}
                    onChange={onUpdateAvailability}
                />
            </GridItem>
            <GridItem>
                <Dropdown
                    aria-label="time"
                    value={time}
                    options={getTimeOptions(t)}
                    onChange={onUpdateTime}
                    isDisabled={isUnavailable}
                />
            </GridItem>
            <GridItem>
                <TimePicker
                    aria-label="from"
                    value={!isAlwaysOn ? localFrom : '00:00'}
                    onChange={onUpdateFrom}
                    isDisabled={isAlwaysOn}
                />
            </GridItem>
            <GridItem>
                <TimePicker
                    aria-label="to"
                    value={!isAlwaysOn ? localTo : '00:00'}
                    onChange={onUpdateTo}
                    isDisabled={isAlwaysOn}
                />
            </GridItem>
        </>
    )
}
